import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type InputHTMLAttributes,
} from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { personName } from '@/i18n/personName';
import type { VirtualAccountDetail } from '@/api/virtualAccounts';
import { aiWalletApi } from '@/api/aiWallet';
import { entitlementsApi } from '@/api/entitlements';
import { ErrorCodes, isApiError, standingRefusal } from '@/api/errors';
import { sharesApi, type Share, type ShareRole } from '@/api/shares';
import { ConfirmPanel } from '@/components/ConfirmDialog';
import { DialogDoneButton } from '@/components/DialogDoneButton';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { ErrorState } from '@/components/ErrorState';
import { StatusChip } from '@/components/StatusChip';
import { useConfirm } from '@/components/useConfirm';
import { applyBackendFields, validationAlert } from '@/features/shared/useFieldErrors';
import { SubscribeInPlace } from '@/features/subscribe/SubscribeInPlace';
import { formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { resolveTokens } from '@/theme/theme';
import { MemberAiLimit } from './MemberAiLimit';
import { RoleToggle } from './RoleToggle';

const SHARE_FIELDS = ['email', 'name'] as const;

const TITLE_ID = 'sharing-dialog-title';
const NOTE_ID = 'sharing-dialog-note';
const CONFIRM_TITLE_ID = 'sharing-dialog-confirm-title';
const CONFIRM_BODY_ID = 'sharing-dialog-confirm-body';
const EMAIL_MAX_LENGTH = 320;
const NAME_MAX_LENGTH = 100;

interface SharingDialogProps {
  detail: VirtualAccountDetail;
  onClose: () => void;
  /**
   * Re-fetch the detail (share count and the audience badge follow): on open, and after a change. It
   * may resolve to `false` when the re-read failed: the open re-read then shows the dialog's error; a
   * write's follow-up ignores it, since the write itself succeeded (TMMAPP-0160 item 7).
   */
  onChanged: () => Promise<boolean | void>;
  /** A snackbar message (a catalog string, already translated). */
  onNotify: (message: string) => void;
  /** No write action at all (the owner of an ARCHIVED virtual account): rows and statuses only. */
  readOnly?: boolean;
  /** The sharer left: the page navigates away. */
  onLeft: () => void;
}

/**
 * The owner removes an active share, withdraws an invitation, or a sharer leaves (their own share
 * id, when the detail carries it). Allowing the assistant on a share confirms too: it is on the
 * owner's AI credit (TMMAPP-0148 U1 item 10; TMMAPI-0184 U3).
 */
type PendingConfirm =
  | { kind: 'remove'; share: Share }
  | { kind: 'withdraw'; share: Share }
  | { kind: 'leave'; ownShareId: number | null }
  | { kind: 'allowAssistant'; share: Share };

type InviteError = 'shareSelf' | 'shareExists' | 'email' | 'rateLimited' | 'generic' | null;

/** The display name, then the email the owner is served, then "A member" (0097 §E 34). */
function shareLabel(share: Share, t: TFunction): string {
  return share.displayName ?? share.email ?? share.boundEmail ?? personName(null, t);
}

function roleLabel(role: ShareRole, t: TFunction): string {
  return t(role === 'MANAGER' ? 'roles.manager' : 'roles.viewer');
}

/**
 * "Who sees {name}" (C4, TMMAPP-0110; design §5.2–§5.4, MM-D-115). Owner: the note and the
 * guest-seat sentence (the served seat price), each ACTIVE row with its seat, status and a Read only /
 * Manager toggle that saves on change, then Remove; each INVITED / EXPIRED row with the toggle, Resend
 * and Withdraw; the add row (their name, email, Access, Add); the legend; Done. Sharer: the ACTIVE
 * co-sharers by display name and role only (MM-D-34) plus the caller's own row with Leave. Full-screen
 * below `md`.
 */
export function SharingDialog({
  detail,
  onClose,
  onChanged,
  onNotify,
  onLeft,
  readOnly = false,
}: SharingDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const tokens = resolveTokens(useTheme());
  const fullScreen = useDialogFullScreen();
  const [email, setEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<ShareRole>('VIEWER');
  const [nameFieldMessage, setNameFieldMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The invite request specifically (Resend shares `busy` but not this button's loading state).
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<InviteError>(null);
  const [inviteFieldMessage, setInviteFieldMessage] = useState<string | null>(null);
  const [inviteSummary, setInviteSummary] = useState<string | null>(null);
  const [rowError, setRowError] = useState(false);
  const pending = useConfirm<PendingConfirm>();
  const [subscribeRequired, setSubscribeRequired] = useState(false);
  // The guest-seat price the owner would be billed (the served price book); no sentence without it.
  const [seatPrice, setSeatPrice] = useState<string | null>(null);
  // The owner's AI wallet currency, the unit of a member's monthly limit (K4); no limit row without it.
  const [walletCurrency, setWalletCurrency] = useState<string | null>(null);
  // A failed wallet read shows the catalog error with Retry in the limit's place, never a vanished
  // control (TMMAPP-0154 item 21); each Retry is a new attempt.
  const [walletRead, setWalletRead] = useState<{ attempt: number; failure: unknown }>({
    attempt: 0,
    failure: null,
  });
  // The members whose limit has an unsaved change: the dialog's guard asks before closing.
  const [unsavedLimits, setUnsavedLimits] = useState<ReadonlySet<number>>(new Set());
  const limitUnsaved = useCallback((shareId: number, unsaved: boolean) => {
    setUnsavedLimits((current) => {
      if (current.has(shareId) === unsaved) return current;
      const next = new Set(current);
      if (unsaved) next.add(shareId);
      else next.delete(shareId);
      return next;
    });
  }, []);
  // Opening re-reads the shares through `onChanged` (TMMAPP-0158 item 5): an invitation accepted
  // since the page loaded shows Active here, never the status the page first read. Once per open.
  // A failed re-read keeps the shares already shown and says so (TMMAPP-0160 item 7).
  const [rereading, setRereading] = useState(true);
  const reread = useRef(false);
  useEffect(() => {
    if (reread.current) return;
    reread.current = true;
    void Promise.resolve()
      .then(onChanged)
      .then((read) => {
        if (read === false) setRowError(true);
      })
      .catch(() => setRowError(true))
      .finally(() => setRereading(false));
  }, [onChanged]);
  const owner = detail.isOwner;
  const anyAssistantGrant =
    owner &&
    !readOnly &&
    detail.shares.some(
      (share) => share.status !== 'REVOKED' && share.seat !== null && share.assistantAllowed,
    );

  useEffect(() => {
    if (!anyAssistantGrant || walletCurrency !== null) return;
    let live = true;
    void aiWalletApi
      .get()
      .then((wallet) => {
        if (live) setWalletCurrency(wallet.currency);
      })
      .catch((failure: unknown) => {
        if (live) setWalletRead((read) => ({ ...read, failure }));
      });
    return () => {
      live = false;
    };
  }, [anyAssistantGrant, walletCurrency, walletRead.attempt]);

  useEffect(() => {
    if (!owner) return;
    let live = true;
    void entitlementsApi
      .get()
      .then((entitlements) => {
        const prices = entitlements.prices;
        const perUnit =
          prices?.seatBands.find((band) => band.perUnitMinor > 0)?.perUnitMinor ?? null;
        if (!live || prices === null || perUnit === null) return;
        setSeatPrice(formatMoneyMinor(perUnit, prices.currency, locale));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [owner, locale]);

  // Who sees it, or will: a removed share stays listed as "Removed" with no actions (TMMAPP-0080).
  const current = detail.shares.filter((share) => share.status !== 'REVOKED');
  // The wallet read's error shows once, under the first member whose limit it would set.
  const firstWalletShareId =
    current.find((share) => share.seat !== null && share.assistantAllowed)?.id ?? null;
  const active = detail.shares.filter((share) => share.status === 'ACTIVE');
  const trimmedEmail = email.trim();
  const trimmedName = inviteName.trim();
  // Dirty while an invite field has text or a member's limit has an unsaved change: the share list
  // itself saves per action.
  const guard = useDialogDirtyGuard(
    trimmedEmail.length > 0 || trimmedName.length > 0 || unsavedLimits.size > 0,
    onClose,
    busy,
  );

  async function handleInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (trimmedEmail.length === 0 || busy) return;
    if (trimmedName.length > NAME_MAX_LENGTH) {
      setNameFieldMessage(t('virtualAccount.inviteNameTooLong'));
      return;
    }
    setBusy(true);
    setInviting(true);
    setInviteError(null);
    setInviteFieldMessage(null);
    setNameFieldMessage(null);
    setInviteSummary(null);
    try {
      const share = await sharesApi.create(detail.id, {
        email: trimmedEmail,
        role: inviteRole,
        ...(trimmedName.length > 0 ? { name: trimmedName } : {}),
      });
      setEmail('');
      setInviteName('');
      setInviteRole('VIEWER');
      await onChanged();
      onNotify(
        share.status === 'ACTIVE'
          ? t('virtualAccount.inviteActive')
          : t('virtualAccount.inviteSent'),
      );
    } catch (error) {
      const code = isApiError(error) ? error.code : ErrorCodes.UNKNOWN;
      if (code === 'SHARE_SELF') setInviteError('shareSelf');
      else if (code === 'SHARE_EXISTS') setInviteError('shareExists');
      else if (code === ErrorCodes.VALIDATION_FAILED) {
        const placed: Partial<Record<(typeof SHARE_FIELDS)[number], string>> = {};
        const result = applyBackendFields(
          error,
          SHARE_FIELDS,
          (field, message) => {
            placed[field] = message;
          },
          t,
          guard.focusErrors,
        );
        if (result.placed === 0 && result.leftover.length === 0) {
          setInviteError('email');
        } else {
          setInviteFieldMessage(placed.email ?? null);
          setNameFieldMessage(placed.name ?? null);
          setInviteSummary(validationAlert(result, error, t, t('virtualAccount.errorEmail')));
        }
      } else if (code === ErrorCodes.RATE_LIMITED) setInviteError('rateLimited');
      else if (code === ErrorCodes.SUBSCRIPTION_REQUIRED) setSubscribeRequired(true);
      else setInviteError('generic');
      if (code !== ErrorCodes.SUBSCRIPTION_REQUIRED) guard.focusErrors();
    } finally {
      setBusy(false);
      setInviting(false);
    }
  }

  async function handleResend(share: Share) {
    if (busy) return;
    setBusy(true);
    setRowError(false);
    try {
      await sharesApi.resend(detail.id, share.id);
      await onChanged();
      onNotify(t('virtualAccount.inviteSent'));
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        setSubscribeRequired(true);
      } else {
        setRowError(true);
      }
    } finally {
      setBusy(false);
    }
  }

  // Each toggle saves (§34 item 9): `PATCH …/shares/{shareId} {role}`, then the detail reloads.
  async function handleRole(share: Share, role: ShareRole) {
    if (busy) return;
    setBusy(true);
    setRowError(false);
    try {
      await sharesApi.updateRole(detail.id, share.id, role);
      await onChanged();
      onNotify(
        t('virtualAccount.sharing.roleSaved', {
          person: shareLabel(share, t),
          role: roleLabel(role, t),
        }),
      );
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        setSubscribeRequired(true);
      } else {
        setRowError(true);
      }
    } finally {
      setBusy(false);
    }
  }

  // "Allow the assistant" on a bound share (TMMAPP-0142 item 8): saves on change, sending only
  // `{assistantAllowed}`. A guest seat can be granted too (TMMAPI-0195 U4, TMMAPP-0164 item 13):
  // asking in shared conversations is on the owner's credit and needs no subscription of their own.
  async function handleAssistant(share: Share, allowed: boolean) {
    if (busy) return;
    setBusy(true);
    setRowError(false);
    try {
      await sharesApi.setAssistantAllowed(detail.id, share.id, allowed);
      await onChanged();
      onNotify(
        t(allowed ? 'assistant.share.allowedToast' : 'assistant.share.revokedToast', {
          name: shareLabel(share, t),
        }),
      );
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        setSubscribeRequired(true);
      } else {
        setRowError(true);
      }
    } finally {
      setBusy(false);
    }
  }

  // A member's monthly limit on the owner's AI credit (K4): `PATCH {assistantMonthlyLimitMinor}`.
  async function handleLimit(share: Share, limitMinor: number | null) {
    if (busy) return;
    setBusy(true);
    setRowError(false);
    try {
      await sharesApi.setAssistantMonthlyLimit(detail.id, share.id, limitMinor);
      await onChanged();
      onNotify(t('assistant.share.limit.saved', { name: shareLabel(share, t) }));
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        setSubscribeRequired(true);
      } else {
        setRowError(true);
      }
      throw error;
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirm() {
    const target = pending.target;
    if (target?.kind === 'allowAssistant') {
      await pending.run(
        async () => {
          await sharesApi.setAssistantAllowed(detail.id, target.share.id, true);
          await onChanged();
          onNotify(t('assistant.share.allowedToast', { name: shareLabel(target.share, t) }));
        },
        (error) => {
          if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
            setSubscribeRequired(true);
            return null;
          }
          return t('virtualAccount.errorGeneric');
        },
      );
      return;
    }
    await pending.run(
      async (action) => {
        if (action.kind === 'allowAssistant') return;
        if (action.kind === 'remove' || action.kind === 'withdraw') {
          await sharesApi.remove(detail.id, action.share.id);
          await onChanged();
          return;
        }
        let shareId = action.ownShareId;
        if (shareId === null) {
          const rows = await sharesApi.sharedWithMe();
          shareId = rows.find((row) => row.virtualAccountId === detail.id)?.shareId ?? null;
        }
        if (shareId === null) throw new Error('no share to leave');
        await sharesApi.remove(detail.id, shareId);
        onLeft();
      },
      // A member removing someone else answers 403 (TMMAPI-0146): all sharing is the owner's.
      (error) =>
        standingRefusal(error) === 'needOwner'
          ? t('roles.removeOthers')
          : t('virtualAccount.errorGeneric'),
    );
  }

  const inviteHelper =
    inviteFieldMessage ??
    (inviteError === 'shareSelf'
      ? t('virtualAccount.errorShareSelf')
      : inviteError === 'shareExists'
        ? t('virtualAccount.errorShareExists')
        : inviteError === 'email'
          ? t('virtualAccount.errorEmail')
          : inviteError === 'rateLimited'
            ? t('virtualAccount.errorRateLimited')
            : undefined);

  const ownRow = detail.shares.find(
    (share) => share.status === 'ACTIVE' && share.userId !== null && share.userId === me.user.id,
  );
  const coSharers = active.filter((share) => share.id !== ownRow?.id);

  // Remove / Withdraw / Leave confirm in place of the dialog's content — dialogs never stack
  // (DLG-005, TMM-B4). Both returns are the same `GuardedDialog`, so the one `Dialog` stays mounted.
  if (pending.target !== null) {
    const target = pending.target;
    const person = target.kind === 'leave' ? null : shareLabel(target.share, t);
    const copy =
      target.kind === 'allowAssistant'
        ? {
            title: t('assistant.share.confirmTitle', { name: person }),
            consequence: t('assistant.share.confirmConsequence'),
            confirm: t('assistant.share.confirmAllow'),
          }
        : target.kind === 'remove'
          ? {
              title: t('virtualAccount.removeShareTitle', { person, name: detail.name }),
              consequence: t('virtualAccount.removeShareConsequence'),
              confirm: t('virtualAccount.removeShareConfirm'),
            }
          : target.kind === 'withdraw'
            ? {
                title: t('virtualAccount.sharing.withdrawTitle', { person }),
                consequence: t('virtualAccount.sharing.withdrawConsequence'),
                confirm: t('virtualAccount.sharing.withdrawConfirm'),
              }
            : {
                title: t('virtualAccount.leaveTitle', { name: detail.name }),
                consequence: t('virtualAccount.leaveConsequence'),
                confirm: t('virtualAccount.leaveConfirm'),
              };
    return (
      <GuardedDialog
        guard={guard}
        confirm={{
          titleId: CONFIRM_TITLE_ID,
          consequenceId: CONFIRM_BODY_ID,
          onClose: pending.busy ? undefined : pending.cancel,
        }}
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreen}
        sx={{ '& .MuiDialog-paper': { maxWidth: fullScreen ? undefined : 520 } }}
      >
        <ConfirmPanel
          titleId={CONFIRM_TITLE_ID}
          consequenceId={CONFIRM_BODY_ID}
          title={copy.title}
          consequence={copy.consequence}
          confirmLabel={copy.confirm}
          destructive={target.kind !== 'allowAssistant'}
          busy={pending.busy}
          error={pending.error}
          focusHeading
          onConfirm={() => void handleConfirm()}
          onCancel={pending.cancel}
        />
      </GuardedDialog>
    );
  }

  const meta = { fontSize: 12, lineHeight: '18px', color: tokens.text2 } as const;

  function shareRow(share: Share) {
    const name = shareLabel(share, t);
    const revoked = share.status === 'REVOKED';
    const pendingInvite = share.status !== 'ACTIVE' && !revoked;
    const seat =
      share.seat === 'SUBSCRIBER'
        ? t('virtualAccount.sharing.seatSubscriber')
        : share.seat === 'GUEST_SEAT'
          ? t('virtualAccount.sharing.seatGuest')
          : null;
    const role = roleLabel(share.role, t);
    return (
      <Box
        component="li"
        key={share.id}
        data-share-row={share.id}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '8px 12px',
          py: 1.5,
          borderBottom: `1px solid ${tokens.line}`,
        }}
      >
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              component="span"
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: share.status === 'ACTIVE' ? tokens.text : tokens.text2,
                overflowWrap: 'anywhere',
              }}
            >
              {name}
            </Typography>
            {share.status !== 'ACTIVE' && <StatusChip domain="share" status={share.status} />}
          </Stack>
          {!revoked && (
            <Typography component="p" sx={{ ...meta, mt: '2px' }}>
              {pendingInvite
                ? t('virtualAccount.sharing.invitedMeta')
                : seat !== null
                  ? t('virtualAccount.sharing.activeMeta', { seat, role })
                  : t('virtualAccount.sharing.activeMetaNoSeat', { role })}
            </Typography>
          )}
        </Box>
        {!readOnly && !revoked && share.seat !== null && (
          <Box sx={{ flex: '1 1 100%', order: 1 }} data-assistant-grant={share.id}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={share.assistantAllowed}
                  disabled={busy}
                  onChange={(event) => {
                    // Turning it on is on the owner's AI credit: it confirms first (item 10).
                    if (event.target.checked) {
                      pending.ask({ kind: 'allowAssistant', share }, `assistant-grant-${share.id}`);
                    } else {
                      void handleAssistant(share, false);
                    }
                  }}
                  slotProps={{
                    input: {
                      'aria-describedby': `assistant-grant-help-${share.id}`,
                      'data-confirm-opener': `assistant-grant-${share.id}`,
                    } as InputHTMLAttributes<HTMLInputElement>,
                  }}
                />
              }
              label={<Typography sx={{ fontSize: 13 }}>{t('assistant.share.allow')}</Typography>}
            />
            <Typography id={`assistant-grant-help-${share.id}`} component="p" sx={meta}>
              {t('assistant.share.allowHelp', { name })}
            </Typography>
            {share.assistantAllowed && walletCurrency !== null && (
              <MemberAiLimit
                shareId={share.id}
                currency={walletCurrency}
                limitMinor={share.assistantMonthlyLimitMinor}
                usedMinor={share.assistantUsedThisMonthMinor}
                busy={busy}
                onSave={(limitMinor) => handleLimit(share, limitMinor)}
                onUnsavedChange={limitUnsaved}
              />
            )}
            {/* One read, one error: under the first member with the assistant allowed. */}
            {share.id === firstWalletShareId &&
              walletCurrency === null &&
              walletRead.failure !== null && (
                <Box sx={{ mt: 1 }} data-member-ai-limit-error={share.id}>
                  <ErrorState
                    variant="section"
                    error={walletRead.failure}
                    what={t('errors.what.aiWallet')}
                    onRetry={() =>
                      setWalletRead((read) => ({ attempt: read.attempt + 1, failure: null }))
                    }
                  />
                </Box>
              )}
          </Box>
        )}
        {!readOnly && !revoked && (
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <RoleToggle
              value={share.role}
              label={t('virtualAccount.sharing.access', { person: name })}
              disabled={busy}
              onChange={(next) => void handleRole(share, next)}
            />
            {pendingInvite && (
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={() => void handleResend(share)}
              >
                {t('virtualAccount.resend')}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              data-confirm-opener={`remove-share-${share.id}`}
              onClick={() =>
                pending.ask(
                  { kind: pendingInvite ? 'withdraw' : 'remove', share },
                  `remove-share-${share.id}`,
                )
              }
              sx={{ color: tokens.text2, borderColor: tokens.line }}
            >
              {pendingInvite ? t('virtualAccount.sharing.withdraw') : t('virtualAccount.remove')}
            </Button>
          </Stack>
        )}
      </Box>
    );
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId={TITLE_ID}
      subtitle={
        <DialogSubtitle id={NOTE_ID}>
          {owner
            ? [
                t('virtualAccount.sharing.note'),
                seatPrice !== null
                  ? t('virtualAccount.sharing.guestSeat', { price: seatPrice })
                  : '',
              ]
                .filter((part) => part.length > 0)
                .join(' ')
            : t('virtualAccount.sharing.note')}
        </DialogSubtitle>
      }
      subtitleId={NOTE_ID}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      sx={{ '& .MuiDialog-paper': { maxWidth: fullScreen ? undefined : 520 } }}
    >
      <DialogTitle id={TITLE_ID}>{t('virtualAccount.whoSees', { name: detail.name })}</DialogTitle>
      <DialogContent aria-busy={rereading || undefined}>
        {rereading && <LinearProgress aria-label={t('common.loading')} data-shares-rereading="" />}
        {subscribeRequired && <SubscribeInPlace onClose={onClose} />}
        {rowError && (
          <Alert severity="error" sx={{ my: 1 }}>
            {t('virtualAccount.errorGeneric')}
          </Alert>
        )}
        {owner ? (
          <>
            <Box sx={{ borderTop: `1px solid ${tokens.line}`, mt: 1 }}>
              {current.length === 0 && (
                <Typography data-share-empty="" sx={{ fontSize: 13, color: tokens.text2, py: 1.5 }}>
                  {t('virtualAccount.sharing.empty')}
                </Typography>
              )}
              {detail.shares.length > 0 && (
                <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                  {detail.shares.map(shareRow)}
                </Box>
              )}
            </Box>
            {!subscribeRequired && !readOnly && (
              <Stack
                component="form"
                aria-label={t('virtualAccount.sharing.addSomeone')}
                direction={{ xs: 'column', sm: 'row' }}
                onSubmit={(event: FormEvent<HTMLFormElement>) => void handleInvite(event)}
                noValidate
                sx={{ gap: 1, mt: 2, alignItems: { sm: 'flex-end' }, flexWrap: 'wrap' }}
              >
                {inviteSummary !== null && (
                  <Alert severity="error" sx={{ width: '100%' }}>
                    {inviteSummary}
                  </Alert>
                )}
                <TextField
                  label={t('virtualAccount.inviteName')}
                  value={inviteName}
                  onChange={(event) => {
                    setInviteName(event.target.value);
                    setNameFieldMessage(null);
                  }}
                  fullWidth
                  error={nameFieldMessage !== null}
                  helperText={nameFieldMessage ?? t('virtualAccount.inviteNameHelper')}
                  slotProps={{
                    htmlInput: { maxLength: NAME_MAX_LENGTH, autoComplete: 'name' },
                  }}
                />
                <TextField
                  label={t('virtualAccount.inviteEmail')}
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  fullWidth
                  error={inviteFieldMessage !== null || inviteError !== null}
                  helperText={
                    inviteError === 'generic' ? t('virtualAccount.errorGeneric') : inviteHelper
                  }
                  slotProps={{
                    htmlInput: { maxLength: EMAIL_MAX_LENGTH, autoComplete: 'email' },
                  }}
                />
                <Stack
                  direction="row"
                  sx={{ alignItems: 'flex-end', gap: 1, flexWrap: 'wrap', pb: { sm: 3 } }}
                >
                  <Box>
                    <Typography component="p" sx={{ ...meta, mb: '4px' }}>
                      {t('virtualAccount.sharing.accessLabel')}
                    </Typography>
                    <RoleToggle
                      value={inviteRole}
                      label={t('virtualAccount.sharing.newAccess')}
                      disabled={busy}
                      onChange={setInviteRole}
                    />
                  </Box>
                  <SubmitButton
                    type="submit"
                    variant="outlined"
                    loading={inviting}
                    disabled={trimmedEmail.length === 0 || busy}
                    sx={{ flexShrink: 0 }}
                  >
                    {t('virtualAccount.sharing.add')}
                  </SubmitButton>
                </Stack>
              </Stack>
            )}
            <Box
              component="dl"
              data-role-legend=""
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: '6px 16px',
                m: 0,
                mt: 2,
                fontSize: 12,
                lineHeight: '18px',
              }}
            >
              <Box component="dt" sx={{ fontWeight: 500, color: tokens.text }}>
                {t('roles.viewer')}
              </Box>
              <Box component="dd" sx={{ m: 0, color: tokens.text2 }}>
                {t('virtualAccount.sharing.legendViewer')}
              </Box>
              <Box component="dt" sx={{ fontWeight: 500, color: tokens.text }}>
                {t('roles.manager')}
              </Box>
              <Box component="dd" sx={{ m: 0, color: tokens.text2 }}>
                {t('virtualAccount.sharing.legendManager')}
              </Box>
            </Box>
          </>
        ) : (
          <List disablePadding>
            {coSharers.map((share) => (
              <ListItem key={share.id} divider disableGutters>
                <ListItemText
                  primary={personName(share.displayName, t)}
                  secondary={roleLabel(share.role, t)}
                />
              </ListItem>
            ))}
            <ListItem
              divider
              disableGutters
              secondaryAction={
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  disabled={busy}
                  data-confirm-opener="leave"
                  onClick={() =>
                    pending.ask({ kind: 'leave', ownShareId: ownRow?.id ?? null }, 'leave')
                  }
                >
                  {t('virtualAccount.leave')}
                </Button>
              }
              sx={{ paddingInlineEnd: (theme) => theme.spacing(14) }}
            >
              <ListItemText primary={me.user.displayName} secondary={t('share.you')} />
            </ListItem>
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <DialogDoneButton onClick={guard.requestClose} disabled={busy} />
      </DialogActions>
    </GuardedDialog>
  );
}
